'use client';

import { useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { PartGroup, GroupFieldType } from '@/services/api/part-groups';
import { useTranslation } from '@/hooks/useTranslation';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

interface DynamicFormProps {
  groups: PartGroup[];
  selectedGroupIds: string[];
  formData: any;
  onFieldChange: (key: string, value: any) => void;
  partNumberValidation?: {
    checking: boolean;
    exists: boolean;
    message: string;
  };
}

// 核心字段定义（键）- 翻译从 t.parts 获取
const getCoreFieldsConfig = (t: any) => [
  { key: 'partNumber', type: 'TEXT', required: true },
  { key: 'partNameEn', type: 'TEXT', required: true },  // 英文名称（必填）
  { key: 'partNameCn', type: 'TEXT', required: false },  // 中文名称（可选）
  { key: 'unit', type: 'TEXT', required: true },
  { key: 'source', type: 'TEXT', required: false },
  { key: 'specifications', type: 'TEXTAREA', required: false },
  { key: 'remark', type: 'TEXTAREA', required: false },
  { key: 'station', type: 'TEXT', required: false },
  { key: 'warehouseLocation', type: 'TEXT', required: false },
  { key: 'currentStock', type: 'NUMBER', required: false },
  { key: 'minStock', type: 'NUMBER', required: false },
  { key: 'maxStock', type: 'NUMBER', required: false },
];

export function DynamicForm({ 
  groups, 
  selectedGroupIds, 
  formData, 
  onFieldChange,
  partNumberValidation
}: DynamicFormProps) {
  const { t, locale } = useTranslation();
  
  // 合并所有字段
  const sections = useMemo(() => {
    const result: any[] = [];
    
    // 1. 核心字段分组
    const coreFieldsConfig = getCoreFieldsConfig(t);
    const coreFields = coreFieldsConfig.map(field => ({
      ...field,
      nameEn: (t.parts as any)[field.key] || field.key,  // 英文名称
      nameCn: (t.parts as any)[field.key] || field.key,  // 中文名称（使用同一翻译）
      name: (t.parts as any)[field.key] || field.key,    // 保留 name 属性兼容性
      placeholder: (t.parts as any)[`${field.key}Placeholder`] || '',
    }));
    
    result.push({
      title: t.parts.coreInfo,
      description: t.parts.coreInfoDesc,
      fields: coreFields,
    });
    
    // 2. 合并所有自定义字段到"分组信息"（排除 "none"）
    const allCustomFields: any[] = [];
    const fieldKeys = new Set<string>(); // 用于去重
    
    selectedGroupIds.filter(id => id !== 'none').forEach(groupId => {
      const group = groups.find(g => g.id === groupId);
      if (group && group.customFields && group.customFields.length > 0) {
        group.customFields.forEach(field => {
          // 避免重复字段
          if (!fieldKeys.has(field.key)) {
            fieldKeys.add(field.key);
            allCustomFields.push({
              key: field.key,
              nameEn: field.nameEn,  // 英文名称（必填）
              nameCn: field.nameCn,  // 中文名称（可选）
              type: field.type,
              required: field.required,
              options: field.options,
              placeholder: field.placeholder,
              helpText: field.helpText,
              isCustom: true,
            });
          }
        });
      }
    });
    
    // 如果有自定义字段，添加"分组信息"区块
    if (allCustomFields.length > 0) {
      result.push({
        title: t.parts.groupInfo,
        description: t.parts.groupInfoDesc,
        fields: allCustomFields,
      });
    }
    
    return result;
  }, [groups, selectedGroupIds, t]);
  
  const renderField = (field: any) => {
    const value = field.isCustom 
      ? formData.customFields?.[field.key] || '' 
      : formData[field.key] || '';
    
    const handleChange = (newValue: any) => {
      if (field.isCustom) {
        const currentCustomFields = formData.customFields || {};
        onFieldChange('customFields', {
          ...currentCustomFields,
          [field.key]: newValue,
        });
      } else {
        onFieldChange(field.key, newValue);
      }
    };
    
    // 零件编号字段特殊处理（显示验证状态）
    const isPartNumberField = field.key === 'partNumber';
    
    switch (field.type) {
      case 'TEXT':
        return (
          <div className="relative">
            <Input
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
              className={`text-sm pr-10 ${
                isPartNumberField && partNumberValidation?.exists
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : ''
              }`}
            />
            {/* 零件编号验证状态图标 */}
            {isPartNumberField && partNumberValidation && value && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {partNumberValidation.checking ? (
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                ) : partNumberValidation.exists ? (
                  <XCircle className="w-4 h-4 text-red-500" />
                ) : (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                )}
              </div>
            )}
          </div>
        );
      
      case 'NUMBER':
      case 'DECIMAL':
        return (
          <Input
            type="number"
            step={field.type === 'DECIMAL' ? '0.01' : '1'}
            value={value}
            onChange={(e) => handleChange(Number(e.target.value))}
            placeholder={field.placeholder}
            required={field.required}
            className="text-sm"
          />
        );
      
      case 'SELECT':
        return (
          <select
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            required={field.required}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t.common.pleaseSelect}</option>
            {field.options?.map((option: string) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case 'MULTISELECT':
        return (
          <select
            multiple
            value={Array.isArray(value) ? value : []}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, option => option.value);
              handleChange(selected);
            }}
            required={field.required}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            size={4}
          >
            {field.options?.map((option: string) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case 'BOOLEAN':
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleChange(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-600">
              {value ? t.common.yes : t.common.no}
            </span>
          </div>
        );
      
      case 'DATE':
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            required={field.required}
            className="text-sm"
          />
        );
      
      case 'DATETIME':
        return (
          <Input
            type="datetime-local"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            required={field.required}
            className="text-sm"
          />
        );
      
      case 'TEXTAREA':
        return (
          <textarea
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            required={field.required}
            placeholder={field.placeholder}
            rows={3}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );
      
      case 'EMAIL':
        return (
          <Input
            type="email"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            className="text-sm"
          />
        );
      
      case 'URL':
        return (
          <Input
            type="url"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            className="text-sm"
          />
        );
      
      case 'PHONE':
        return (
          <Input
            type="tel"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            className="text-sm"
          />
        );
      
      default:
        return (
          <Input
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            className="text-sm"
          />
        );
    }
  };
  
  return (
    <div className="space-y-8">
      {sections.map((section, idx) => (
        <div key={idx} className="space-y-4">
          <div className="border-b pb-2">
            <h3 className="text-lg font-semibold text-gray-900">
              {section.title}
            </h3>
            {section.description && (
              <p className="text-sm text-gray-600 mt-1">
                {section.description}
              </p>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {section.fields.map((field: any) => (
              <div 
                key={field.key} 
                className={
                  field.type === 'TEXTAREA' || field.type === 'MULTISELECT' 
                    ? 'md:col-span-2' 
                    : ''
                }
              >
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {locale === 'zh' ? (field.nameCn || field.nameEn) : field.nameEn}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {renderField(field)}
                {/* 零件编号验证错误提示 */}
                {field.key === 'partNumber' && partNumberValidation?.exists && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <XCircle className="w-3 h-3" />
                    {partNumberValidation.message}
                  </p>
                )}
                {field.helpText && (
                  <p className="text-xs text-gray-500 mt-1">{field.helpText}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

